import React from "react";
import { URLS } from "Utils/apiURL";
import { MAGIC_FIELDS } from "Pages/Dashboard/data-toolkit/constants";
import StatusPill from "Pages/Dashboard/data-toolkit/StatusPill";
import ToolRunner from "Pages/Dashboard/data-toolkit/ToolRunner";

const cellHas = (row, idx) =>
  idx != null && ((row && row[idx]) || "").trim() !== "";

const rowUsable = (row, m) => {
  const hasName =
    cellHas(row, m.fullName) ||
    (cellHas(row, m.firstName) && cellHas(row, m.lastName));
  const hasCompany = cellHas(row, m.companyDomain) || cellHas(row, m.companyName);
  return (
    cellHas(row, m.email) ||
    cellHas(row, m.linkedinUrl) ||
    cellHas(row, m.companyDomain) ||
    cellHas(row, m.companyLinkedin) ||
    (hasName && hasCompany)
  );
};

const hasMagicColumns = m => {
  const hasName =
    m.fullName != null || (m.firstName != null && m.lastName != null);
  const hasCompany = m.companyDomain != null || m.companyName != null;
  return (
    m.email != null ||
    m.linkedinUrl != null ||
    m.companyDomain != null ||
    m.companyLinkedin != null ||
    (hasName && hasCompany)
  );
};

const fieldVal = (r, key) => (r.fields && r.fields[key]) || "";
const textCol = (header, key) => ({
  header,
  cell: r => {
    const v = fieldVal(r, key);
    return v ? (
      <span className="dtTrunc" title={v}>
        {v}
      </span>
    ) : (
      "-"
    );
  }
});
const linkCol = (header, key) => ({
  header,
  cell: r =>
    fieldVal(r, key) ? (
      <a href={fieldVal(r, key)} target="_blank" rel="noopener noreferrer">
        View
      </a>
    ) : (
      "-"
    )
});

// Recommendation pill (reuses the status-pill palette).
const REC_CLASS = {
  Revive: "valid",
  "New contact": "valid",
  "Re-route": "risky",
  Replace: "running",
  Drop: "notfound"
};
const recCol = {
  header: "Recommendation",
  cell: r => {
    const v = fieldVal(r, "recommendation");
    return v ? (
      <span className={`dtStatusPill ${REC_CLASS[v] || "notfound"}`}>{v}</span>
    ) : (
      "-"
    );
  }
};

const MAGIC_STATS = (counts, job) => [
  { label: "Enriched", value: counts.found || 0 },
  ...(counts.alternates
    ? [{ label: "New contacts", value: counts.alternates }]
    : []),
  { label: "No match", value: counts.notFound || 0 },
  ...(counts.skipped ? [{ label: "Skipped", value: counts.skipped }] : []),
  { label: "Credits used", value: (job && job.creditsCharged) || 0, brand: true }
];

// Replacement-contact toggle (one per company). Rendered in the mapping step.
const magicConfigPanel = (config, setConfig) => (
  <div className="dtMagicConfig">
    <div className="dtCheckRow">
      <input
        type="checkbox"
        id="dtFindAlt"
        checked={!!config.findAlternates}
        onChange={e => setConfig({ ...config, findAlternates: e.target.checked })}
      />
      <label htmlFor="dtFindAlt">
        Find one replacement per company when a contact has moved or is
        unreachable <span className="dtCreditBadge">recommended</span>
      </label>
    </div>
    {config.findAlternates && (
      <div className="dtDropSub dtMagicAltNote">
        We look for one strong match at the account, in the same department as
        the contact you lost, and prefer someone with a valid email. A
        replacement is 2 credits, and only when we find one. If no suitable
        person exists, nothing is charged for it.
      </div>
    )}
  </div>
);

const MAGIC_COLUMNS = [
  { header: "Status", cell: r => <StatusPill status={r.emailStatus} /> },
  recCol,
  textCol("Clean name", "cleanName"),
  textCol("Title", "title"),
  textCol("Seniority", "seniority"),
  textCol("Person industry", "personIndustry"),
  textCol("Current company", "currentCompany"),
  textCol("Job changed", "jobChanged"),
  textCol("Job change detail", "jobChangeDetail"),
  {
    header: "Best email",
    cell: r =>
      r.fields && r.fields.email ? (
        <a className="dtTrunc" title={r.fields.email} href={`mailto:${r.fields.email}`}>
          {r.fields.email}
        </a>
      ) : (
        "-"
      )
  },
  {
    header: "Email status",
    cell: r =>
      r.fields && r.fields.emailStatus ? (
        <StatusPill status={r.fields.emailStatus} />
      ) : (
        "-"
      )
  },
  linkCol("LinkedIn", "linkedin"),
  textCol("Clean company", "cleanCompany"),
  textCol("Clean domain", "cleanDomain"),
  textCol("Company industry", "companyIndustry"),
  textCol("Company size", "companySize"),
  textCol("Company revenue", "companyRevenue"),
  textCol("Company type", "companyType"),
  textCol("Magic notes", "notes")
];

const CONFIG = {
  toolKey: "DATA_MAGIC",
  iconKey: "data-magic",
  title: "Data magic",
  description:
    "Paste a messy list or CRM export. We clean it, enrich each person and company, detect who has changed jobs, and recommend who to revive, re-route or replace.",
  fields: MAGIC_FIELDS,
  endpoint: URLS.dataToolkitDataMagic,
  runNoun: "Data magic run",
  runLabel: "Run magic",
  foundLabel: "Enriched",
  inputSub:
    "Paste rows or upload a CSV with whatever columns you have. We figure out the rest.",
  pastePlaceholder:
    "name, company, linkedin url, email\nElon Musk, Tesla, https://linkedin.com/in/elonmusk, elon@tesla.com",
  mappingHint: (
    <>
      Magic uses whatever it can find: a <b>name</b>, <b>email</b>,{" "}
      <b>LinkedIn URL</b>, <b>website</b>, or <b>company</b>. Map what you have.
    </>
  ),
  requiredCheck: hasMagicColumns,
  requiredError:
    "Map at least one identifier: an email, a LinkedIn URL, a website/domain, or a name with a company.",
  defaultConfig: {
    findAlternates: true
  },
  configPanel: magicConfigPanel,
  // Up to 3 per contact: profile, company, and a found email. A moved contact
  // spends these on their new company.
  rowCredits: (row, m) => (rowUsable(row, m) ? 3 : 0),
  // Replacement contacts: up to one per usable company, 2 credits each, only
  // charged when we find one.
  configCredits: (config, usableRows) =>
    config && config.findAlternates ? usableRows * 2 : 0,
  costNote: max => (
    <>
      Up to <b>{max.toLocaleString()}</b> credit{max === 1 ? "" : "s"} for this
      run: up to 3 per contact (profile, company, and a found email), plus 2 for
      each replacement we find. You're only charged for results we deliver.
    </>
  ),
  stats: MAGIC_STATS,
  columns: MAGIC_COLUMNS
};

const DataMagic = () => <ToolRunner cfg={CONFIG} />;

export default DataMagic;
